import { Combobox } from "@dashboard/components/Combobox";
import { Box, Input, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { Control, Controller } from "react-hook-form";
import { useIntl } from "react-intl";

import { messages } from "./messages";
import { CompanyInformationFormValues } from "./types";

export const COMPANY_INFORMATION_FORM_ID = "wizard-company-information-form";

interface CompanyInformationFormProps {
  control: Control<CompanyInformationFormValues>;
  countryChoices: Array<{ label: string; value: string }>;
  disabled: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Box
  >
    <Text
      size={2}
      fontWeight="bold"
      __textTransform="uppercase"
      __letterSpacing="0.06em"
      color="default1"
    >
      {title}
    </Text>
    <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
      {children}
    </Box>
  </Box>
);

export const CompanyInformationForm: React.FC<CompanyInformationFormProps> = ({
  control,
  countryChoices,
  disabled,
  onSubmit,
}) => {
  const intl = useIntl();

  return (
    <form id={COMPANY_INFORMATION_FORM_ID} onSubmit={onSubmit}>
      <Box display="flex" flexDirection="column" __gap="24px">
        {/* Business Details Section */}
        <SectionCard title={intl.formatMessage(messages.businessDetailsSection)}>
          <Controller
            name="companyName"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={intl.formatMessage(messages.companyName)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={intl.formatMessage(messages.companyDescription)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
        </SectionCard>

        {/* Address Section */}
        <SectionCard title={intl.formatMessage(messages.addressSection)}>
          <Controller
            name="streetAddress1"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={intl.formatMessage(messages.streetAddress)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="streetAddress2"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={intl.formatMessage(messages.streetAddressLine2)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
          <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={3}>
            <Controller
              name="city"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={intl.formatMessage(messages.city)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
            <Controller
              name="postalCode"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={intl.formatMessage(messages.postalCode)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          </Box>
          <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={3}>
            <Controller
              name="country"
              control={control}
              render={({ field: { value, onChange, ...rest }, fieldState }) => (
                <Combobox
                  {...rest}
                  value={
                    countryChoices.find(c => c.value === value) ?? null
                  }
                  fetchOptions={() => undefined}
                  onChange={e => onChange(e.target.value ?? "")}
                  options={countryChoices}
                  label={intl.formatMessage(messages.country)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
            <Controller
              name="countryArea"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={intl.formatMessage(messages.stateProvince)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={disabled}
                />
              )}
            />
          </Box>
        </SectionCard>

        {/* Contact Section */}
        <SectionCard title={intl.formatMessage(messages.contactSection)}>
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                label={intl.formatMessage(messages.phone)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
        </SectionCard>
      </Box>
    </form>
  );
};
